import React from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { colors } from '@/styles';
import { DETAILS_LABEL, INTANGIBLE_LABEL, ITEM_TAG_COLORS } from '../constants';
import { itemFullName, itemStatusTag } from '../utils';
import { CardNameTooltip } from './CardNameTooltip';
import {
    Card,
    CardAside,
    CardBody,
    CardGrip,
    CardDescription,
    CardInfoButton,
    CardPart,
    CardQuantity,
    CardTag,
    CardTags,
    CardTitle,
} from './blocks';

/**
 * One item card on the split board: line number, SKU, description, the arrival tag, and the card's quantity.
 * A card that holds part of a divided line says which line it is part of. Hovering the name shows it in full, and the
 * info button opens the line's details.
 *
 * @param {{ item: { item_id: string, sku?: string, description?: string, intangible?: boolean }, lineNumber: number, quantity: number, divided?: boolean, actions?: React.ReactNode, onShowDetails?: () => void, tooltipSuppressed?: boolean }} props
 *   `item` is the parent line from the order read. `quantity` is this card's portion. `divided` is true when the line has more than one card.
 *   `actions` render under the tags, for example the Move to menu. `onShowDetails` shows the info button and opens
 *   the details dialog. `tooltipSuppressed` keeps the name tooltip shut, for example during a drag.
 */
export const ItemCard = ({
    item,
    lineNumber,
    quantity,
    divided = false,
    actions = null,
    onShowDetails,
    tooltipSuppressed = false,
}) => {
    const tag = itemStatusTag(item);
    const name = item.sku || item.description || 'Item';
    return (
        <Card aria-label={`Line ${lineNumber}: ${name}`} data-testid={`split-card-${item.item_id}`}>
            <CardGrip aria-hidden="true">⋮⋮</CardGrip>
            <CardBody>
                {divided ? <CardPart>Part of line {lineNumber}</CardPart> : null}
                <CardNameTooltip title={itemFullName(item)} suppressed={tooltipSuppressed}>
                    <CardTitle>{`${lineNumber} · ${name}`}</CardTitle>
                    {item.description ? <CardDescription>{item.description}</CardDescription> : null}
                </CardNameTooltip>
                <CardTags>
                    <CardTag data-testid={`item-tag-${item.item_id}`} tagColor={ITEM_TAG_COLORS[tag.key]}>
                        {tag.label}
                    </CardTag>
                    {item.intangible ? (
                        <CardTag tagColor={colors.greys.primary}>
                            {' · '}
                            {INTANGIBLE_LABEL}
                        </CardTag>
                    ) : null}
                </CardTags>
                {actions}
            </CardBody>
            <CardAside>
                <CardQuantity>×{quantity}</CardQuantity>
                {onShowDetails ? (
                    <CardInfoButton
                        size="small"
                        aria-label={`${DETAILS_LABEL}: line ${lineNumber}, ${name}`}
                        onClick={onShowDetails}
                    >
                        <InfoOutlinedIcon fontSize="small" />
                    </CardInfoButton>
                ) : null}
            </CardAside>
        </Card>
    );
};

export default ItemCard;
